import type { Actor } from 'domains-work-order-authorization';
import type { UserType } from 'lane-shared/types/User';
import type { UserGroupRoleType } from 'lane-shared/types/UserGroupRole';
import { safeConvertToUUID } from 'uuid-encoding';

export type ActorUser = Pick<UserType, '_id' | 'isSuperUser' | 'roles'>;

// PermissionKey is a string union; widening it avoids casting at the call site.
const permissionsOf = (role: UserGroupRoleType): string[] =>
  role.groupRole.permissions;

export const isActive = (
  role: Pick<UserGroupRoleType, 'expires'>,
  now: Date
): boolean => !role.expires || new Date(role.expires) >= now;

const coversChannel = (role: UserGroupRoleType, channelId: string): boolean => {
  const channel = role.groupRole.channel;

  return (
    safeConvertToUUID(channel?._id) === channelId ||
    Boolean(
      channel?.hierarchy?.hierarchyDown?.some(
        id => safeConvertToUUID(id) === channelId
      )
    )
  );
};

export const toActor = (
  user: ActorUser | null | undefined,
  now: Date
): Actor => {
  const rolesOn = (channelId: string): UserGroupRoleType[] => {
    const id = safeConvertToUUID(channelId);

    return (user?.roles ?? []).filter(
      role => isActive(role, now) && coversChannel(role, id)
    );
  };

  return {
    userId: safeConvertToUUID(user?._id ?? ''),
    isSuperUser: Boolean(user?.isSuperUser),
    hasPermission: (keys, channelId) =>
      rolesOn(channelId).some(role =>
        keys.some(key => permissionsOf(role).includes(key))
      ),
    groupRoleIds: channelId =>
      rolesOn(channelId).map(role => safeConvertToUUID(role.groupRole._id)),
  };
};
